// ── Number formatting ───────────────────────────────────────────────────────

export function formatCost(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

export function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export function formatPercent(n: number): string {
  if (n < 0) return '—';
  return `${Math.round(n * 100)}%`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

// ── Footer line ─────────────────────────────────────────────────────────────

export function formatFooterLine(stats: {
  cacheHitRate: number;
  toolCallCount: number;
  avgToolDurationMs: number;
  errorCount: number;
}): string {
  const parts: string[] = [];

  // Cache hit rate — pi doesn't show this
  if (stats.cacheHitRate >= 0) {
    const emoji = stats.cacheHitRate >= 0.8 ? '⚡' : stats.cacheHitRate >= 0.5 ? '🔋' : '⚠️';
    parts.push(`${emoji} cache ${formatPercent(stats.cacheHitRate)}`);
  }

  // Tool calls — pi doesn't show this
  if (stats.toolCallCount > 0) {
    parts.push(`🔧 ${stats.toolCallCount} tools`);
  }

  // Average tool duration
  if (stats.avgToolDurationMs > 0) {
    parts.push(`⏱ ${formatDuration(stats.avgToolDurationMs)} avg`);
  }

  // Errors
  if (stats.errorCount > 0) {
    parts.push(`❌ ${stats.errorCount} err`);
  }

  return parts.join('  ');
}

// ── ASCII table for /stats submenus ────────────────────────────────────────

export function formatTable(headers: string[], rows: string[][]): string {
  const colWidths = headers.map((h, i) => {
    const maxRow = Math.max(h.length, ...rows.map(r => r[i]?.length ?? 0));
    return Math.min(maxRow + 2, 30);
  });

  const headerLine = headers.map((h, i) => h.padEnd(colWidths[i])).join('');
  const sepLine = colWidths.map(w => '─'.repeat(w)).join('');
  const bodyLines = rows.map(r => r.map((cell, i) => (cell ?? '').padEnd(colWidths[i])).join(''));

  return [headerLine, sepLine, ...bodyLines].join('\n');
}